import * as React from "react";
import { Input } from "@/components/ui";
import { BuilderState, Destination, FieldKey, FIELD_KEYS, FormRouteBuilder } from "../useFormRouteBuilder";

const ROW_LABELS: Record<FieldKey, string> = {
  player: "Player (who's asking)",
  fan: "SocialCoach Fan ID",
  client_id: "Client ID",
  client_email: "Client email",
  lang: "Language",
};

export const MERGE_FIELD_COPY: Record<Destination, { title: string; description: string; column: string }> = {
  totalexpert: {
    title: "What TotalExpert fills in",
    description:
      "Each value comes from a TotalExpert merge field. Check the names against your account; you can edit them here.",
    column: "TotalExpert merge field",
  },
  crm: {
    title: "What your CRM fills in",
    description: "Paste the merge field your CRM uses for each value.",
    column: "Your CRM's merge field",
  },
  website: {
    title: "What your page fills in",
    description: "Example values. Replace them with the real ones when you render the page.",
    column: "Example value",
  },
};

export function visibleFieldKeys(state: BuilderState): FieldKey[] {
  return FIELD_KEYS.filter(key => (key === "player" ? state.playerMode === "sender" : state.enabled[key]));
}

/** The input id for a field's mapping, so the route status can focus the one that's missing. */
export function mergeFieldInputId(key: FieldKey) {
  return `form-routes-merge-field-${key}`;
}

interface Props {
  state: BuilderState;
  setValue: FormRouteBuilder["setValue"];
}

export function MergeFieldTable({ state, setValue }: Props) {
  const copy = MERGE_FIELD_COPY[state.destination];
  const rows = visibleFieldKeys(state);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-slate-500 leading-relaxed">{copy.description}</p>
      <table className="w-full text-sm" data-testid="merge-field-table">
        <thead>
          <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-400">
            <th className="pb-2 pr-3 font-bold w-2/5">SocialCoach gets</th>
            <th className="pb-2 font-bold">{copy.column}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={2} className="py-3 text-slate-500">
                Nothing to fill in. Tick a client field below or choose "Whoever sends the email".
              </td>
            </tr>
          ) : (
            rows.map(key => (
              <tr key={key}>
                <td className="py-2 pr-3 font-medium text-slate-700">{ROW_LABELS[key]}</td>
                <td className="py-2">
                  <Input
                    id={mergeFieldInputId(key)}
                    aria-label={ROW_LABELS[key]}
                    value={state.values[key]}
                    onChange={e => setValue(key, e.target.value)}
                    placeholder={state.destination === "crm" ? "e.g. {{Contact.Email}}" : undefined}
                    className="font-mono text-xs bg-white"
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
